/**
 * Badge
 *
 * Compact label primitives for status, ownership, and metadata.
 *
 * Primary responsibilities:
 * - Provide legacy badge variants for existing surfaces
 * - Provide semantic StatusBadge tones for Operating Surface UI
 *
 * Dependencies:
 * - class-variance-authority
 * - components/ui/platform-tokens
 * - lib/utils
 */

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import {
  platformToneClasses,
  type PlatformDensity,
  type PlatformTone
} from '@/components/ui/platform-tokens'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'focus:ring-ring inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 motion-safe:transition-colors motion-safe:duration-200 motion-reduce:transition-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80 border-transparent',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 border-transparent',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/80 border-transparent',
        outline: 'text-foreground',
        muted: platformToneClasses.neutral.badge,
        success: platformToneClasses.success.badge,
        warning: platformToneClasses.warning.badge,
        danger: platformToneClasses.danger.badge,
        info: platformToneClasses.info.badge,
        agent: platformToneClasses.agent.badge
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />
}

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: PlatformTone
  density?: Extract<PlatformDensity, 'compact' | 'default'>
  dot?: boolean
}

function StatusBadge({
  className,
  children,
  tone = 'neutral',
  density = 'default',
  dot = false,
  ...props
}: StatusBadgeProps) {
  const toneClasses = platformToneClasses[tone]

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full border font-medium leading-none',
        density === 'compact' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        toneClasses.badge,
        className
      )}
      {...props}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={cn('size-1.5 shrink-0 rounded-full', toneClasses.badgeDot)}
        />
      )}
      <span className="truncate">{children}</span>
    </span>
  )
}

export { Badge, StatusBadge, badgeVariants }
